// 负责浏览器侧 checkout API 调用，与 useStorefront 和 Pages Functions 交易接口关联。
import type { CartItem } from "../domain/cart";
import type {
  CustomerDetails,
  MockPaymentOutcome,
  Order
} from "../domain/orders";

export type CheckoutMode = "browser" | "server";

type CheckoutSessionResponse = {
  session?: {
    provider?: string;
    orderId?: string;
    redirectView?: string;
  };
  order?: unknown;
};

type PaymentEventResponse = {
  event?: unknown;
  order?: unknown;
};

type ApiErrorResponse = {
  error?: unknown;
};

// 读取 checkout 模式，未显式配置时保留本地浏览器 Mock 链路。
export const getCheckoutMode = (): CheckoutMode => {
  const mode = import.meta.env.VITE_CHECKOUT_MODE;

  if (!mode) {
    return "browser";
  }

  if (mode === "browser" || mode === "server") {
    return mode;
  }

  throw new Error(
    `Unsupported VITE_CHECKOUT_MODE: ${mode}. Use browser or server.`
  );
};

// 调用服务端接口创建 checkout session，并返回服务端落库后的订单。
export const createServerCheckoutSession = async (
  cartItems: CartItem[],
  customer: CustomerDetails
): Promise<Order> => {
  const response = await requestJson<CheckoutSessionResponse>(
    "/api/checkout/session",
    {
      cartItems,
      customer
    }
  );

  if (response.session?.redirectView !== "mock-checkout") {
    throw new Error("Checkout session did not return the mock checkout view.");
  }

  return assertOrder(response.order);
};

// 调用服务端 Mock 支付事件接口，并返回更新后的订单。
export const resolveServerMockPayment = async (
  orderId: string,
  outcome: MockPaymentOutcome
): Promise<Order> => {
  const response = await requestJson<PaymentEventResponse>(
    "/api/payment-events/mock",
    {
      orderId,
      outcome
    }
  );

  return assertOrder(response.order);
};

// 发送 JSON POST 请求，失败时带出服务端错误消息。
const requestJson = async <ResponseBody>(
  url: string,
  body: unknown
): Promise<ResponseBody> => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
  const payload = parseJson(await response.text());

  if (!response.ok) {
    throw new Error(readApiError(payload));
  }

  return payload as ResponseBody;
};

// 解析 JSON 响应，非 JSON 时给出明确错误。
const parseJson = (text: string): ApiErrorResponse => {
  try {
    return JSON.parse(text) as ApiErrorResponse;
  } catch {
    throw new Error("Checkout API response must be JSON.");
  }
};

// 读取 API 错误消息，缺失时使用状态无关的明确错误。
const readApiError = (payload: ApiErrorResponse): string => {
  if (typeof payload.error === "string" && payload.error.trim()) {
    return payload.error;
  }

  return "Checkout API request failed.";
};

// 校验服务端返回的订单结构，避免 UI 消费未知响应。
const assertOrder = (value: unknown): Order => {
  if (!isRecord(value)) {
    throw new Error("Checkout API response is missing order.");
  }

  if (
    typeof value.id !== "string" ||
    !Array.isArray(value.lines) ||
    !isRecord(value.totals) ||
    !isRecord(value.customer) ||
    typeof value.paymentStatus !== "string" ||
    typeof value.fulfillmentStatus !== "string" ||
    !Array.isArray(value.paymentEventIds) ||
    typeof value.createdAt !== "string" ||
    typeof value.updatedAt !== "string"
  ) {
    throw new Error("Checkout API response returned an invalid order.");
  }

  return value as Order;
};

// 判断未知值是否为普通对象。
const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};
